import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    ArrowLeft, RefreshCcw, CheckCircle2, Clock, UserPlus, XCircle,
    Eye, AlertCircle,
} from "lucide-react";

/**
 * /action-centre — one unified feed with a 5-verb action bar.
 */
const PRIORITY_STYLE = {
    critical: { bg: "bg-rose-50",   fg: "text-rose-700",   b: "border-rose-200" },
    high:     { bg: "bg-amber-50",  fg: "text-amber-700",  b: "border-amber-200" },
    medium:   { bg: "bg-sky-50",    fg: "text-sky-700",    b: "border-sky-200" },
    low:      { bg: "bg-stone-100", fg: "text-stone-700",  b: "border-stone-200" },
};

const ACTIONS = [
    { key: "review",  label: "Review",   Icon: Eye },
    { key: "done",    label: "Done",     Icon: CheckCircle2 },
    { key: "remind",  label: "Remind",   Icon: Clock },
    { key: "assign",  label: "Assign",   Icon: UserPlus },
    { key: "ignore",  label: "Ignore",   Icon: XCircle },
];

export default function ActionCentre() {
    const [data, setData]     = useState(null);
    const [kind, setKind]     = useState("");
    const [remindAt, setRemindAt] = useState({});
    const [assignee, setAssignee] = useState({});

    const load = async () => {
        try {
            const r = await api.get("/api/action-centre",
                { params: { kind: kind || undefined } });
            setData(r.data);
        } catch { toast.error("Failed to load"); }
    };
    useEffect(() => { load(); }, [kind]);

    const act = async (item, action) => {
        const body = { action };
        if (action === "remind") {
            const at = remindAt[item.item_id];
            if (!at) { toast.error("Pick a remind date first"); return; }
            body.remind_at = at;
        }
        if (action === "assign") {
            const who = assignee[item.item_id];
            if (!who) { toast.error("Enter an assignee first"); return; }
            body.assignee = who;
        }
        try {
            await api.post(`/api/action-centre/${encodeURIComponent(item.item_id)}/action`, body);
            toast.success(`Marked ${action}`);
            load();
        } catch { toast.error("Action failed"); }
    };

    return (
        <div data-testid="action-centre" className="min-h-screen bg-[#f6f3ec] text-[#0b1e3f]">
            <div className="max-w-5xl mx-auto p-6">
                <div className="flex items-center gap-3 mb-6">
                    <Link to="/dashboard" className="text-stone-500 hover:text-stone-800"><ArrowLeft size={18} /></Link>
                    <div>
                        <div className="text-[10.5px] uppercase tracking-widest text-[#a0894a]">Action Centre</div>
                        <h1 className="font-['Fraunces',serif] text-2xl">Everything asking for your attention</h1>
                    </div>
                    <Button variant="outline" size="sm" className="ml-auto" onClick={load}>
                        <RefreshCcw size={14} className="mr-1" /> Refresh
                    </Button>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                    {["", "payment_due", "renewal", "exception", "document_expiry",
                       "rewards_expiring", "inbox_review"].map(k => (
                        <button key={k || "all"} data-testid={`ac-filter-${k || "all"}`}
                            onClick={() => setKind(k)}
                            className={`px-3 py-1 text-sm rounded-full transition ${
                                kind === k ? "bg-[#0b1e3f] text-[#f6f3ec]"
                                           : "text-stone-700 border border-stone-200 hover:bg-stone-100"}`}>
                            {k ? k.replace(/_/g, " ") : "All"}
                        </button>
                    ))}
                </div>

                {data && data.items.length === 0 && (
                    <Card><CardContent className="py-10 text-center text-stone-500">
                        <AlertCircle size={24} className="mx-auto mb-2 text-[#c9a253]" />
                        Nothing needs your attention right now.
                    </CardContent></Card>
                )}

                {data && data.items.map(item => {
                    const s = PRIORITY_STYLE[item.priority] || PRIORITY_STYLE.low;
                    return (
                        <Card key={item.item_id} data-testid={`ac-item-${item.item_id}`}
                              className={`mb-3 ${s.bg} ${s.b}`}>
                            <CardContent className="pt-4 pb-4">
                                <div className="flex items-start gap-3">
                                    <span className={`text-[10px] font-semibold uppercase tracking-widest ${s.fg} mt-1`}>
                                        {item.priority}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-['Fraunces',serif] text-base">{item.title}</div>
                                        <div className="text-sm text-stone-600 mt-0.5">{item.detail}</div>
                                        <div className="text-[11px] text-stone-500 mt-1">
                                            {item.kind} · {item.source} {item.at && `· ${item.at}`}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2 mt-3">
                                            {ACTIONS.map(a => (
                                                <Button key={a.key} size="sm" variant="outline"
                                                    data-testid={`ac-action-${a.key}-${item.item_id}`}
                                                    onClick={() => act(item, a.key)}>
                                                    <a.Icon size={12} className="mr-1" /> {a.label}
                                                </Button>
                                            ))}
                                            <Input placeholder="Remind date"
                                                value={remindAt[item.item_id] || ""}
                                                onChange={e => setRemindAt({ ...remindAt, [item.item_id]: e.target.value })}
                                                className="max-w-[160px] h-8 text-xs" type="date" />
                                            <Input placeholder="Assignee email"
                                                value={assignee[item.item_id] || ""}
                                                onChange={e => setAssignee({ ...assignee, [item.item_id]: e.target.value })}
                                                className="max-w-[180px] h-8 text-xs" />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
}
